import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  GlobalStyles,
  Paper
} from '@mui/material';
import { ArrowRight, CheckCircle } from '@mui/icons-material';
import { motion } from 'framer-motion';
import getStyles from './styles';
import { useNavigate } from 'react-router-dom';

const globalStyles = (
  <GlobalStyles
    styles={{
      '@keyframes glow': {
        '0%': {
          opacity: 0.6,
          boxShadow: '0 0 20px rgba(79, 70, 229, 0.3)'
        },
        '100%': {
          opacity: 1,
          boxShadow: '0 0 30px rgba(79, 70, 229, 0.6)'
        }
      },
      '@keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(30px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      },
      '@keyframes fadeInScale': {
        '0%': {
          opacity: 0,
          transform: 'scale(0.8)'
        },
        '100%': {
          opacity: 1,
          transform: 'scale(1)'
        }
      }
    }}
  />
);

const timelineSteps = [
  { day: 'Day 1', label: 'Project Kickoff & Content Submission' },
  { day: 'Day 2', label: 'Wireframe & Design Mockups' },
  { day: 'Day 3', label: 'Homepage Design Finalization' },
  { day: 'Day 4', label: 'Inner Pages Layout' },
  { day: 'Day 5', label: 'Responsive Design Review' },
  { day: 'Day 6', label: 'Content Integration' },
  { day: 'Day 7', label: 'Testing & Performance Optimization' },
  { day: 'Day 8', label: 'SEO Setup & Analytics' },
  { day: 'Day 9', label: 'Client Review & Final Edits' },
  { day: 'Day 10', label: 'Website LIVE Launch' }
];

const TenDaysMVPBanner = () => {
  const [scrollY, setScrollY] = useState(0);
  const styles = getStyles();
  const [page, setPage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleStartProject = () => {
    navigate('/contact');
  };

  const handleViewPricing = () => {
    console.log('View Pricing');
  };

  const stepsPerPage = 5;
  const totalPages = Math.ceil(timelineSteps.length / stepsPerPage);

  const currentSteps = timelineSteps.slice(
    page * stepsPerPage,
    (page + 1) * stepsPerPage
  );

  const handleNext = () => {
    if (page < totalPages - 1) setPage(page + 1);
  };

  const handlePrev = () => {
    if (page > 0) setPage(page - 1);
  };

  const TimelineStep = ({ day, label, delay = false }) => (
    <Box
      sx={{
        ...styles.timelineStep,
        animation: `fadeInScale 0.6s ease-out ${delay ? '0.2s' : '0s'} both`
      }}
    >
      <Box sx={styles.stepIconContainer}>
        <CheckCircle sx={styles.stepIcon} />
      </Box>
      <Box sx={styles.stepContent}>
        <Typography variant="body1" sx={styles.stepDay}>
          {day}
        </Typography>
        <Typography variant="body2" sx={styles.stepLabel}>
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box component="section" sx={styles.heroSection}>
      {globalStyles}
      <Box
        sx={styles.parallaxBackground}
        style={{
          transform: `translateY(${scrollY * 0.5}px)`
        }}
      />
      <Box sx={styles.gradientOverlay} />
      <Container maxWidth="lg" sx={styles.container}>
        <Box sx={styles.content}>
          <Typography variant="h1" sx={styles.mainHeadline}>
            <Typography component="span" sx={styles.headlineLine1}>
              Get Your Website
            </Typography>
            <Typography component="span" sx={styles.headlineLine2}>
              <span style={styles.textGradient}>LIVE</span> in Just{' '}
              <span style={styles.textGlow}>10 Days</span>
            </Typography>
            <Typography component="span" sx={styles.subtitleLine}>
              Or Your Money Back!
            </Typography>
          </Typography>
          <Typography sx={styles.subtitle}>
            <span style={styles.subtitleHighlight}>Amizhth Express:</span> Fast,
            Professional Websites Ready in Just 48 Hours.{' '}
            <span style={styles.accentText}>No Compromises, No Delays.</span>
          </Typography>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box sx={styles.ctaContainer}>
              <Button
                sx={styles.primaryButton}
                onClick={() => handleStartProject()}
              >
                Start My 10-Days Website Launch
                <motion.span
                  sx={styles.buttonIcon}
                  animate={{ x: 0 }}
                  whileHover={{ x: 5 }}
                >
                  <ArrowRight />
                </motion.span>
              </Button>
              <Button sx={styles.outlineButton} onClick={handleViewPricing}>
                View Pricing
              </Button>
            </Box>
          </motion.div>
          <Box sx={styles.timelineContainer}>
            <Paper sx={styles.timelineCard}>
              <Typography variant="h3" sx={styles.timelineTitle}>
                Your 10-Days Website Launch Journey
              </Typography>
              <Box sx={styles.timelinePaginationWrapper}>
                <Button
                  sx={{
                    ...styles.arrowButton,
                    visibility: page === 0 ? 'visible' : 'visible'
                  }}
                  onClick={handlePrev}
                >
                  ◀
                </Button>
                <Box sx={styles.timelineBox}>
                  <Box sx={styles.timelineContent}>
                    {currentSteps.map((step, index) => (
                      <React.Fragment key={step.day}>
                        <TimelineStep
                          day={step.day}
                          label={step.label}
                          delay={index % 2 !== 0}
                        />
                        {index < currentSteps.length - 1 && (
                          <Box sx={styles.timelineConnector} />
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
                <Button
                  sx={{
                    ...styles.arrowButton,
                    visibility: page === totalPages - 1 ? 'visible' : 'visible'
                  }}
                  onClick={handleNext}
                >
                  ▶
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TenDaysMVPBanner;
